import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isToday, isYesterday } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

import {
  getMessages,
  getRestaurantMessages,
  markMessagesAsRead,
  type Message,
  saveMessage,
  sendMassMessage,
  MessageSender,
  OptionalRestaurantMessageOptions,
} from './apiHelpers';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { parseToShortDateFormat } from '@shared/utils/dateFormatters';

export const MESSAGING_PARAM = 'messaging';
export const MESSAGE_GUEST = 'true';
export const GUEST_ID_PARAM = 'guestId';
export const WALK_IN_ID_PARAM = 'walkInId';

const useFetchMessages = (userId: string) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['messages', userId],
    queryFn: () => getMessages(restaurantId, userId),
    refetchInterval: 5000,
    initialData: [],
  });
};

export const useRestaurantMessagesQuery = (
  customOptions: OptionalRestaurantMessageOptions = {},
) => {
  const restaurant = useRestaurant();

  const deriveDateGroup = (time: string): string => {
    const date = new Date(time);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return parseToShortDateFormat(time);
  };

  const options = {
    limit: customOptions.limit ?? 10,
    unreadOnly: customOptions.unreadOnly ?? true,
    sender: customOptions.sender ?? MessageSender.Guest,
  };

  return useQuery({
    queryKey: ['messages', restaurant.id, options],
    queryFn: () => getRestaurantMessages(restaurant.id, options),
    select: (data) => {
      return data.map((notification) => {
        return {
          ...notification,
          dateGroup: deriveDateGroup(notification.createdAt),
        };
      });
    },
    refetchInterval: 30_000,
  });
};

const useSaveMessage = (userId: string, onSuccessCallback: () => void) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ message }: { message: string }) =>
      saveMessage(restaurantId, userId, message),
    onSuccess: (newMessage) => {
      queryClient.setQueryData(['messages', userId], (oldData: Message[]) => [
        ...(oldData ?? []),
        newMessage,
      ]);
      onSuccessCallback();
    },
    onError: () => {
      errorToast({ message: 'Failed to send message. Please try again.' });
    },
  });
};

const useSendMassMessage = (onSuccessCallback: () => void) => {
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: ({
      message,
      userIds,
    }: {
      message: string;
      userIds: string[];
    }) => sendMassMessage(restaurantId, userIds, message),
    onSuccess: () => {
      successToast({ message: 'Message(s) sent successfully.' });
      onSuccessCallback();
    },
    onError: () => {
      errorToast({ message: 'Failed to send message(s). Please try again.' });
    },
  });
};

const useMarkMessagesAsRead = () => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      guestId,
      messages,
    }: {
      guestId: string;
      messages: Message[];
    }) =>
      markMessagesAsRead(
        restaurantId,
        guestId,
        messages.map((m) => m.id),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', restaurantId],
        refetchType: 'active',
      });
    },
  });
};

const useMessagingDrawer = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isOpen =
    searchParams.get(MESSAGING_PARAM) === MESSAGE_GUEST &&
    (searchParams.has(GUEST_ID_PARAM) || searchParams.has(WALK_IN_ID_PARAM));

  // We need to filter here because `getAll` might return a single element array with an empty string
  const guestIds = searchParams.getAll(GUEST_ID_PARAM).filter(Boolean);
  const walkInIds = searchParams.getAll(WALK_IN_ID_PARAM).filter(Boolean);

  const openMessagingDrawer = (guestIds: string[], walkInIds?: string[]) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(MESSAGING_PARAM, MESSAGE_GUEST);
      guestIds.forEach((id) => {
        params.append(GUEST_ID_PARAM, id);
      });
      walkInIds?.forEach((id) => {
        params.append(WALK_IN_ID_PARAM, id);
      });
      return params;
    });
  };

  const closeMessagingDrawer = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete(MESSAGING_PARAM);
      params.delete(GUEST_ID_PARAM);
      params.delete(WALK_IN_ID_PARAM);
      return params;
    });
  };

  const messagingDrawerSearchParams = (
    guestIds: string[],
    walkInIds?: string[],
  ) =>
    new URLSearchParams({
      [MESSAGING_PARAM]: MESSAGE_GUEST,
      [GUEST_ID_PARAM]: guestIds.join(','),
      [WALK_IN_ID_PARAM]: walkInIds?.join(',') ?? '',
    });

  return {
    isOpen,
    guestIds,
    walkInIds,
    openMessagingDrawer,
    closeMessagingDrawer,
    messagingDrawerSearchParams,
  };
};

export {
  useFetchMessages,
  useSaveMessage,
  useMarkMessagesAsRead,
  useMessagingDrawer,
  useSendMassMessage,
};
